import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { toast } from "sonner";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface Paciente {
  id: string;
  user_id?: string;
  nome: string;
  email: string;
  telefone: string;
  data_nascimento?: string;
  ultima_consulta?: string;
  status: "Ativo" | "Inativo";
  endereco?: string;
  cep?: string;
  cpf?: string;
  autoriza_email?: boolean;
  autoriza_whatsapp?: boolean;
  autoriza_marketing?: boolean;
  optout_email?: boolean;
  optout_whatsapp?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePacienteData {
  nome: string;
  email: string;
  telefone: string;
  data_nascimento?: string;
  ultima_consulta?: string;
  status: "Ativo" | "Inativo";
  endereco?: string;
  cep?: string;
  cpf?: string;
  autoriza_email?: boolean;
  autoriza_whatsapp?: boolean;
  autoriza_marketing?: boolean;
  optout_email?: boolean;
  optout_whatsapp?: boolean;
}

export function usePacientes() {
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const fetchPacientes = async () => {
    if (!user) {
      setPacientes([]);
      return;
    }
    try {
      setLoading(true);
      const data = await apiList<Paciente>("pacientes");
      setPacientes(data.filter((p) => p.status !== "Inativo").sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (error: any) {
      toast.error("Erro ao carregar pacientes: " + error.message);
      setPacientes([]);
    } finally {
      setLoading(false);
    }
  };

  const createPaciente = async (pacienteData: CreatePacienteData) => {
    if (!user) {
      toast.error("Usuario nao autenticado");
      return null;
    }
    try {
      const data = await apiCreate<Paciente>("pacientes", pacienteData);
      setPacientes((prev) => [...prev, data].sort((a, b) => a.nome.localeCompare(b.nome)));
      toast.success("Paciente criado com sucesso!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao criar paciente: " + error.message);
      throw error;
    }
  };

  const updatePaciente = async (id: string, pacienteData: Partial<CreatePacienteData>) => {
    try {
      const data = await apiUpdate<Paciente>("pacientes", id, pacienteData);
      setPacientes((prev) => prev.map((p) => (p.id === id ? data : p)).sort((a, b) => a.nome.localeCompare(b.nome)));
      toast.success("Paciente atualizado com sucesso!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao atualizar paciente: " + error.message);
      throw error;
    }
  };

  const deletePaciente = async (id: string) => {
    try {
      await apiDelete("pacientes", id);
      setPacientes((prev) => prev.filter((p) => p.id !== id));
      toast.success("Paciente excluido com sucesso!");
    } catch (error: any) {
      toast.error("Erro ao excluir paciente: " + error.message);
      throw error;
    }
  };

  useEffect(() => {
    fetchPacientes();
  }, [user?.id, activeClinicaId]);

  return { pacientes, loading, createPaciente, updatePaciente, deletePaciente, refetch: fetchPacientes };
}
